import { Sparkles, X } from "lucide-react";

import { cn } from "@/lib/utils";
import type { Tag } from "@/types";

/**
 * Hash a string into a number deterministically.
 * Same tag label → same color, no matter the conversation.
 */
function hash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

/**
 * Curated tag color pairs (border + bg + text), tuned for our dark surface.
 * Avoid cyan because that's reserved for Allison.
 */
const PALETTES = [
  // [border, bg, text]
  ["border-violet-500/30", "bg-violet-500/10", "text-violet-300"],
  ["border-pink-500/30", "bg-pink-500/10", "text-pink-300"],
  ["border-orange-500/30", "bg-orange-500/10", "text-orange-300"],
  ["border-amber-500/30", "bg-amber-500/10", "text-amber-300"],
  ["border-emerald-500/30", "bg-emerald-500/10", "text-emerald-300"],
  ["border-rose-500/30", "bg-rose-500/10", "text-rose-300"],
  ["border-indigo-500/30", "bg-indigo-500/10", "text-indigo-300"],
  ["border-teal-500/30", "bg-teal-500/10", "text-teal-300"],
] as const;

/**
 * Special tags that override the hash-based color. Use this for tags
 * that need to carry semantic urgency (red for "urgente", green for "vip").
 */
const SEMANTIC_OVERRIDES: Record<string, [string, string, string]> = {
  urgente: ["border-destructive/40", "bg-destructive/10", "text-destructive"],
  vip: ["border-success/40", "bg-success/10", "text-success"],
  "monto alto": ["border-warning/40", "bg-warning/10", "text-warning"],
};

function colorsFor(label: string) {
  const override = SEMANTIC_OVERRIDES[label.toLowerCase()];
  if (override) return override;
  return PALETTES[hash(label) % PALETTES.length];
}

interface TagChipProps {
  tag: Tag;
  /** Show an X to remove the tag. */
  onRemove?: () => void;
  /** Smaller variant for dense lists. */
  size?: "sm" | "md";
  className?: string;
}

export function TagChip({
  tag,
  onRemove,
  size = "md",
  className,
}: TagChipProps) {
  const [border, bg, text] = colorsFor(tag.label);
  const isMarcus = tag.source === "marcus";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-medium",
        size === "sm" ? "px-1.5 py-0 text-[10px]" : "px-2 py-0.5 text-[11px]",
        border,
        bg,
        text,
        className
      )}
    >
      {isMarcus && (
        <Sparkles
          className={cn(size === "sm" ? "size-2.5" : "size-3", "shrink-0")}
          aria-label="Tag automática (Marcus)"
        />
      )}
      <span className="lowercase">{tag.label}</span>
      {onRemove && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 inline-flex items-center justify-center rounded-full opacity-60 transition-opacity hover:opacity-100"
          aria-label={`Quitar tag ${tag.label}`}
        >
          <X className={size === "sm" ? "size-2.5" : "size-3"} />
        </button>
      )}
    </span>
  );
}
